/**
 * Web App Tracking Service
 * Sends events to API. Fire-and-forget; never blocks UI or throws.
 * Regression-proof: silently no-ops when API is unavailable.
 */

export type EventType =
  | 'page_view'
  | 'section_view'
  | 'cta_click'
  | 'demo_form_submit'
  | 'download_link_click';

export type ViewType = 'main' | 'privacy' | 'download' | 'demo' | 'home' | 'events' | 'event' | 'booked' | 'childSafety';

export type DeviceType = 'mobile' | 'desktop' | 'tablet' | 'unknown';

function getDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) {
    return /iPad|Tablet/i.test(ua) ? 'tablet' : 'mobile';
  }
  return 'desktop';
}

let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    sessionId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
  return sessionId;
}

export interface TrackPayload {
  sessionId?: string;
  eventType: EventType;
  view?: ViewType;
  section?: string;
  eventData?: Record<string, unknown>;
}

/** Track an event. Never throws; silently fails if API unavailable. */
export function track(payload: TrackPayload): void {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl || apiUrl.trim() === '') {
    return;
  }

  const body = {
    sessionId: getSessionId(),
    eventType: payload.eventType,
    view: payload.view,
    section: payload.section,
    eventData: payload.eventData ?? {},
    device: getDeviceType(),
  };

  fetch(`${apiUrl.replace(/\/$/, '')}/api/v1/web-app/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // Silently ignore
  });
}
